type FormatType = 'number' | 'currency' | 'percent' | 'date' | 'datetime' | 'bytes';

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

// ISO 4217 zero-decimal currencies — no fractional digits by standard
const ZERO_DECIMAL_CURRENCIES = new Set([
  'KRW', 'JPY', 'VND', 'IDR', 'BIF', 'CLP', 'GNF', 'ISK',
  'KMF', 'MGA', 'PYG', 'RWF', 'UGX', 'UYI', 'VUV', 'XAF', 'XOF', 'XPF',
]);

/**
 * Format a value according to a format hint string.
 *
 * Supported formats: `"number"`, `"currency"`, `"currency:EUR"`, `"percent"`,
 * `"date"`, `"datetime"`, `"bytes"`. Returns `String(value)` for unknown formats.
 *
 * @param value - The value to format (coerced to number where needed).
 * @param format - Format hint, optionally with a parameter after `:` (e.g., `"currency:USD"`).
 * @param locale - BCP 47 locale tag for number/currency formatting. Falls back to browser default.
 * @returns Formatted string. Null/undefined values render as `—` (em-dash) so
 * empty cells in tables/metrics are visually distinct from "0".
 *
 * @example
 * ```ts
 * formatValue(1234.5, 'number')       // "1,234.5"
 * formatValue(1234.5, 'currency:EUR') // "€1,234.50"
 * formatValue(73, 'percent')          // "73%"
 * formatValue(1536000, 'bytes')       // "1.5 MB"
 * formatValue(null, 'currency:KRW')   // "—"
 * ```
 */
export function formatValue(value: unknown, format?: string, locale?: string): string {
  if (value == null) return '—';

  // Parse format string: 'currency:USD' → type='currency', param='USD'
  const [type, param] = format?.split(':') ?? [];

  switch (type as FormatType) {
    case 'number':
      return formatNumber(value, locale);
    case 'currency':
      return formatCurrency(value, param, locale);
    case 'percent':
      return formatPercent(value, locale);
    case 'date':
      return formatDate(value, locale);
    case 'datetime':
      return formatDatetime(value, locale);
    case 'bytes':
      return formatBytes(value);
    default:
      return String(value);
  }
}

function formatNumber(value: unknown, locale?: string): string {
  const num = Number(value);
  if (isNaN(num)) return String(value);
  return new Intl.NumberFormat(locale).format(num);
}

function formatCurrency(value: unknown, currencyCode?: string, locale?: string): string {
  const num = Number(value);
  if (isNaN(num)) return String(value);
  const currency = currencyCode || 'USD';
  try {
    const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase());
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...(isZeroDecimal && {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    }).format(num);
  } catch {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  }
}

function formatPercent(value: unknown, locale?: string): string {
  const num = Number(value);
  if (isNaN(num)) return String(value);
  if (locale) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'percent',
        maximumFractionDigits: 2,
      }).format(num / 100);
    } catch {
      // Invalid locale fallback
    }
  }
  return num + '%';
}

function formatDate(value: unknown, locale?: string): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    if (locale) {
      try {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          return new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).format(d);
        }
      } catch {
        // Invalid locale fallback
      }
    }
    return value.slice(0, 10);
  }
  return String(value);
}

function formatDatetime(value: unknown, locale?: string): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    if (locale) {
      try {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          return new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }).format(d);
        }
      } catch {
        // Invalid locale fallback
      }
    }
    return value.slice(0, 16).replace('T', ' ');
  }
  return String(value);
}

function formatBytes(value: unknown): string {
  const raw = Number(value);
  if (isNaN(raw)) return String(value);

  const sign = raw < 0 ? '-' : '';
  let num = Math.abs(raw);

  let i = 0;
  while (num >= 1024 && i < BYTE_UNITS.length - 1) {
    num /= 1024;
    i++;
  }
  return sign + (i === 0 ? num : num.toFixed(1)) + ' ' + BYTE_UNITS[i];
}
